import * as React from 'react';
import {
  Routes,
  Route,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Container } from 'react-grid-system';
import { IconVariants } from '../components/atoms/icons/types';
import Typography from '../components/atoms/Typography';
import PageHeader, { IButton } from '../components/molecules/PageHeader';
import OrderReturnPage from '../pages/OrderReturnPage';
import ProductPage from '../pages/ProductPage';
import ProductDetailsPage from '../pages/ProductDetailsPage';
import ReturnBasketPage from '../pages/ReturnBasketPage';
import ReturnFreightPage from '../pages/ReturnFreightPage';
import ReturnSummaryPage from '../pages/ReturnSummaryPage';
import ReturnCompletePage from '../pages/ReturnCompletePage';
import LoginPage from '../pages/LoginPage';
import { useAppInit } from '../contexts/appInitContext';
import { useReturnContext } from '../contexts/returnContext';
import { useSettings } from '../contexts/settingsContext';
import ReturnByFilePage from '../pages/ReturnByFilePage';

export enum EASYCOM_ROUTES {
  HOME = '/',
  LOGIN = '/login',
  ORDER_PAGE = '/order',
  PRODUCT_PAGE = '/product',
  RETURNS = '/returns',
  RETURNS_FILEUPLOAD = '/returns/fileupload',
  RETURNS_FREIGHT = '/returns/freight',
  RETURNS_SUMMARY = '/returns/summary',
  RETURNS_COMPLETE = '/returns/complete',
  CATCH_ALL = '*',
}

const NonExisting = () => (
  <Typography translateKey="404" variant="h1" align="center" />
);

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { params } = useAppInit();
  const { redirectUrl: redirectFromInit } = params;
  const { setShowSpecificReturn } = useReturnContext();
  const { redirectUrl: redirectFromSettings } = useSettings();

  const { returns } = useReturnContext();
  const noOfReturnItems = Object.keys(returns).length;

  const showBackButton = (): {
    icon: IconVariants;
    title: string;
    onClick: () => void;
  } => {
    switch (location.pathname) {
      case EASYCOM_ROUTES.RETURNS:
        return {
          icon: 'arrow-left',
          title: 'create_returns',
          onClick: () => navigate(-1),
        };
      case EASYCOM_ROUTES.RETURNS_FILEUPLOAD:
        return {
          icon: 'arrow-left',
          title: 'create_returns',
          onClick: () => navigate(-1),
        };
      case EASYCOM_ROUTES.RETURNS_FREIGHT:
        return {
          icon: 'arrow-left',
          title: 'create_returns',
          onClick: () => navigate(-1),
        };
      case EASYCOM_ROUTES.RETURNS_SUMMARY:
        return {
          icon: 'arrow-left',
          title: 'freight_options',
          onClick: () => navigate(-1),
        };
      default:
        return undefined;
    }
  };

  const primaryButton = (): IButton => {
    switch (location.pathname) {
      case EASYCOM_ROUTES.RETURNS_COMPLETE:
        return {
          icon: 'arrow-right',
          title: 'leave_iframe',
          onClick: () => {
            let newHref = redirectFromSettings;
            if (redirectFromInit) {
              newHref = redirectFromInit;
            }

            window.parent.location = newHref;
            return null;
          },
        };
      case EASYCOM_ROUTES.RETURNS:
      case EASYCOM_ROUTES.RETURNS_FREIGHT:
      case EASYCOM_ROUTES.RETURNS_SUMMARY:
        return undefined;
      default:
        return {
          icon: 'package',
          title: 'return_basket',
          translateParams: { counter: noOfReturnItems },
          onClick: () => {
            setShowSpecificReturn(undefined);
            navigate(EASYCOM_ROUTES.RETURNS);
          },
        };
    }
  };

  return (
    <Container>
      <PageHeader
        title="managing_returns"
        buttonLeft={showBackButton()}
        buttonRight={primaryButton()}
      />
      <Outlet />
    </Container>
  );
};

const AppNavigator: React.FC = () => (
  <Routes>
    <Route path={EASYCOM_ROUTES.HOME} element={<Layout />}>
      <Route path={EASYCOM_ROUTES.HOME} element={<OrderReturnPage />} />
      <Route path={EASYCOM_ROUTES.ORDER_PAGE} element={<OrderReturnPage />} />
      <Route path={EASYCOM_ROUTES.RETURNS} element={<ReturnBasketPage />} />
      <Route path={EASYCOM_ROUTES.RETURNS_FILEUPLOAD} element={<ReturnByFilePage />} />
      <Route
        path={EASYCOM_ROUTES.RETURNS_FREIGHT}
        element={<ReturnFreightPage />}
      />
      <Route
        path={EASYCOM_ROUTES.RETURNS_SUMMARY}
        element={<ReturnSummaryPage />}
      />
      <Route
        path={EASYCOM_ROUTES.RETURNS_COMPLETE}
        element={<ReturnCompletePage />}
      />
      <Route path={EASYCOM_ROUTES.PRODUCT_PAGE} element={<ProductPage />} />
      <Route
        path={`${EASYCOM_ROUTES.PRODUCT_PAGE}/:productNumber`}
        element={<ProductDetailsPage />}
      />
      <Route
        path={`${EASYCOM_ROUTES.PRODUCT_PAGE}/:productNumber/:variant`}
        element={<ProductDetailsPage />}
      />
      <Route path={EASYCOM_ROUTES.CATCH_ALL} element={<NonExisting />} />
    </Route>
    <Route path={EASYCOM_ROUTES.LOGIN} element={<LoginPage />} />
  </Routes>
);

export default AppNavigator;
